import HeaderBrand from "@/shared/layout/Header/HeaderBrand";
import { WHITE_LABEL_CONFIG } from "@/whiteLable";
import React, { useState } from "react";
import { createPortal } from "react-dom";

import { getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import { useSecureAccess } from "@/shared/permissionBase";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

export default function AuthSidebar(): React.JSX.Element {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const { navItems: filteredNavItems } = useSecureAccess();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [tooltip, setTooltip] = useState<{ label: string; top: number; left: number } | null>(null);

  const sidebarClasses = getThemeComponentGroup("sidebar");
  const sidebarBgClass = sidebarClasses?.bgColor || "bg-theme-surface-subtle";
  const sidebarTextClass = sidebarClasses?.textColor || "text-theme-text";
  const activeItemBgClass = sidebarClasses?.activeItemBgColor || "bg-theme-primary";
  const activeItemTextClass = sidebarClasses?.activeItemTextColor || "text-theme-text-inverse";
  const inactiveItemTextClass = sidebarClasses?.inactiveItemTextColor || "text-theme-text-muted";
  const toggleButtonBgClass =
    sidebarClasses?.toggleButtonBgColor ||
    "border border-theme-border bg-theme-surface-subtle hover:bg-theme-surface-muted";
  const toggleIconClass = sidebarClasses?.toggleIconColor || "text-theme-text";

  const getActiveTab = () => {
    const currentPath = pathname.split("?")[0];
    const activeItem = filteredNavItems.find(
      (item) => currentPath === item.path || currentPath.startsWith(`${item.path}/`)
    );
    return activeItem?.id ?? "";
  };

  const callOnTabChange = (item: { path: string }) => {
    navigate(item.path);
  };

  const toggleSidebar = () => {
    setIsCollapsed((value) => !value);
  };

  return (
    <aside
      className={`
        relative inset-y-0 left-0 z-40 flex transform flex-col overflow-hidden border-r border-theme-border/70 transition-all duration-300 ease-in-out md:translate-x-0
        ${sidebarBgClass} ${sidebarTextClass} ${sidebarClasses?.root || ""}
        ${isCollapsed ? "w-16" : "w-65"}
      `}
    >
      <div className="pointer-events-none absolute inset-0 bg-theme-surface via-transparent to-theme-accent/10" />

      <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto thin-scrollbar">
        <div className={`mb-4 mt-5 ${isCollapsed ? "mx-1 flex justify-center" : "mx-6"}`}>
          {isCollapsed ? (
            <div className="flex scale-50 origin-center justify-center">
              <img
                src={WHITE_LABEL_CONFIG.app.faviconPath}
                alt={WHITE_LABEL_CONFIG.brand.logo.alt}
                className="h-10 w-20 object-contain"
              />
            </div>
          ) : (
            <HeaderBrand variant="sidebar" />
          )}
        </div>

        <nav className={`flex-1 space-y-1 ${isCollapsed ? "px-2" : "px-3"}`}>
          {filteredNavItems.map((item) => {
            const isActive = getActiveTab() === item.id;

            return (
              <button
                key={item.id}
                id={`sidebar-item-${item.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => callOnTabChange(item)}
                onMouseEnter={(event) => {
                  if (!isCollapsed) return;
                  const rect = event.currentTarget.getBoundingClientRect();
                  setTooltip({
                    label: item.label,
                    top: rect.top + rect.height / 2,
                    left: rect.right + 10,
                  });
                }}
                onMouseLeave={() => setTooltip(null)}
                title={isCollapsed ? item.label : undefined}
                className={`group relative flex w-full items-center gap-3 rounded-lg py-2.5 text-left transition-all ${
                  isCollapsed ? "justify-center px-2" : "px-3"
                } ${
                  isActive
                    ? `${activeItemBgClass} font-medium ${activeItemTextClass} ${sidebarClasses?.activeItem || ""}`
                    : `${inactiveItemTextClass} hover:bg-theme-text/5 ${sidebarClasses?.inactiveItem || ""}`
                }`}
              >
                {isActive && !isCollapsed && (
                  <span className="absolute inset-y-1 right-0 w-1.5 rounded-l-full bg-theme-accent" />
                )}
                <item.icon size={18} strokeWidth={2} className="shrink-0" />
                {!isCollapsed && <span className="text-sm">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className={`py-4 ${isCollapsed ? "flex justify-center" : "flex justify-end pr-4"}`}>
          <button
            id="admin-sidebar-toggle"
            onClick={toggleSidebar}
            className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${toggleButtonBgClass} ${sidebarClasses?.toggleButton || ""}`}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight size={20} className={toggleIconClass} />
            ) : (
              <ChevronLeft size={20} className={toggleIconClass} />
            )}
          </button>
        </div>
      </div>

      {tooltip &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[100] -translate-y-1/2 whitespace-nowrap rounded-md bg-theme-text px-2.5 py-1.5 text-xs font-medium text-theme-text-inverse shadow-lg"
            style={{ top: tooltip.top, left: tooltip.left }}
          >
            {tooltip.label}
          </div>,
          document.body
        )}
    </aside>
  );
}
